import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import api from "../../../../services/api";
import { useCompanies } from "../../../../hooks/companies";
import { useDateFilter } from "../../../../hooks/datefilter";

// Resumo da aba "Cotação" da Torre (PO1-3628, épico PO1-3620).
//
// Os cards do Cotado × Realizado e do Savings leem o MESMO objeto: uma chamada
// a /indicadores/gobid/cotado-realizado/resumo e uma a /indicadores/gobid/savings/cards
// por recorte (embarcador + período), compartilhadas por cache em módulo, no
// molde de componentsDespacho/useResumoDespacho. Sem cálculo próprio: o número
// do card é o do indicador de origem, para o mesmo período.
//
// Contrato de orquestração da Torre (`deferLoad` / `onRegisterFetch`): com
// deferLoad a PRIMEIRA carga fica com a fila da página; depois toda troca de
// recorte carrega na hora. Sem props (Meu Painel) carrega ao montar.

const DIAS_PADRAO = 30;
const DATA = "YYYY-MM-DD";

const cache = new Map(); // chave -> { promessa, dados, assinantes }

export function limparCacheResumoCotacao() {
  cache.clear();
}

export function periodoCotacao(startDate, endDate) {
  const inicio = startDate ? moment(startDate) : moment().subtract(DIAS_PADRAO, "days");
  const fim = endDate ? moment(endDate) : moment();
  return { desde: inicio.format(DATA), ate: fim.format(DATA) };
}

export function chaveRecorte(empresaId, periodo) {
  return `${empresaId === null || empresaId === undefined ? "nenhum" : empresaId}|${periodo.desde}|${periodo.ate}`;
}

function entradaDe(chave) {
  let entrada = cache.get(chave);
  if (!entrada) {
    entrada = { promessa: null, dados: null, assinantes: 0 };
    cache.set(chave, entrada);
  }
  return entrada;
}

const semErro = (p) => p.then((res) => ({ data: res.data, erro: null })).catch((e) => ({ data: null, erro: e }));

// Busca os dois indicadores; um falhar não derruba o outro.
async function obterResumo(empresaId, periodo) {
  const params = { idEmbarcador: empresaId, desde: periodo.desde, ate: periodo.ate };
  const [cotado, savings] = await Promise.all([
    semErro(api.get("/indicadores/gobid/cotado-realizado/resumo", { params })),
    semErro(api.get("/indicadores/gobid/savings/cards", { params })),
  ]);
  return { cotado: cotado.data, erroCotado: cotado.erro, savings: savings.data, erroSavings: savings.erro };
}

// Devolve { dados, carregando, erro, periodo, empresaId, semEmbarcador, recarregar }.
export default function useResumoCotacao({ deferLoad = false, onRegisterFetch } = {}) {
  const { empresa, empresaId, isAllEmpresas, hasEmpresaSelection } = useCompanies();
  const { startDate, endDate } = useDateFilter();

  const periodo = useMemo(() => periodoCotacao(startDate, endDate), [startDate, endDate]);
  const semEmbarcador = !hasEmpresaSelection || Boolean(isAllEmpresas);
  const chave = chaveRecorte(semEmbarcador ? null : empresaId, periodo);

  const [estado, setEstado] = useState(() => {
    const entrada = cache.get(chave);
    return { chave, dados: entrada && entrada.dados ? entrada.dados : null, carregando: false, erro: null };
  });

  const montado = useRef(true);
  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  useEffect(() => {
    const entrada = entradaDe(chave);
    entrada.assinantes += 1;
    return () => {
      entrada.assinantes -= 1;
      if (entrada.assinantes <= 0 && cache.get(chave) === entrada) cache.delete(chave);
    };
  }, [chave]);

  const carregar = useCallback(
    async (forcar = false) => {
      if (semEmbarcador) {
        if (montado.current) setEstado({ chave, dados: null, carregando: false, erro: null });
        return null;
      }
      const entrada = entradaDe(chave);
      if (forcar) {
        entrada.dados = null;
        entrada.promessa = null;
      }
      if (entrada.dados) {
        if (montado.current) setEstado((atual) => (atual.chave === chave && atual.dados === entrada.dados ? atual : { chave, dados: entrada.dados, carregando: false, erro: null }));
        return entrada.dados;
      }
      if (!entrada.promessa) {
        entrada.promessa = obterResumo(empresaId, periodo)
          .then((dados) => {
            entrada.dados = dados;
            return dados;
          })
          .catch((e) => {
            entrada.promessa = null;
            throw e;
          });
      }
      if (montado.current) setEstado((atual) => ({ ...atual, chave, carregando: true, erro: null }));
      try {
        const dados = await entrada.promessa;
        if (montado.current) setEstado({ chave, dados, carregando: false, erro: null });
        return dados;
      } catch (e) {
        if (montado.current) setEstado({ chave, dados: null, carregando: false, erro: e });
        return null;
      }
    },
    [chave, empresaId, periodo, semEmbarcador]
  );

  const jaCarregou = useRef(false);
  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(() => {
        jaCarregou.current = true;
        return carregar();
      });
    }
    if (!deferLoad || jaCarregou.current) {
      jaCarregou.current = true;
      carregar();
    }
  }, [carregar, deferLoad, onRegisterFetch, empresa]);

  const recarregar = useCallback(() => carregar(true), [carregar]);
  const dadosDoRecorte = estado.chave === chave ? estado.dados : null;
  return { dados: dadosDoRecorte, carregando: estado.carregando, erro: estado.erro, periodo, empresaId, semEmbarcador, recarregar };
}
